import IconAnimatedSpinner from 'assets/icons/animated-spinner.svg';
import IconChevronLeft from 'assets/icons/chevron-left.svg';
import IconSend from 'assets/icons/send.svg';
import cn from 'classnames';
import Alert from 'components/alert';
import ImpressionTrackerNew from 'components/impression-tracker/new-impression-tracker';
import { usePetContext } from 'contexts/pet';
import useMobile from 'hooks/common/use-mobile';
import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { gtm, selectElement } from 'tracking';
import { QAData, catQAItems, dogQAItems } from './qa';
import QAItem from './qa-items';

interface UserQuestion {
  question: string;
  showAnswer: boolean;
}

const TRACKING_NAME = 'AI Bot PDP Test';

/**
 * AiBotTest component that displays preset questions with expandable answers
 * and allows users to submit custom questions that get error responses.
 *
 * NOTE: Text content is currently in German as this is an AB test feature.
 * If fully deployed, these texts need to be internationalized using react-intl.
 */
const AiBotTest: React.FC = () => {
  const { current } = usePetContext();
  const isMobile = useMobile();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [userQuestions, setUserQuestions] = useState<UserQuestion[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<'preset' | 'user'>('preset');
  const containerRef = useRef<HTMLDivElement>(null);

  const qaItems: QAData[] = useMemo(
    () => (current === 'dogs' ? dogQAItems : catQAItems),
    [current]
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
  };

  const scrollToComponent = useCallback(() => {
    if (containerRef.current) {
      try {
        const headerHeight = parseInt(
          getComputedStyle(document.documentElement).getPropertyValue(
            '--header-height'
          ) || '64',
          10
        );

        const componentTop = containerRef.current.getBoundingClientRect().top;
        const scrollPosition = window.pageYOffset + componentTop - headerHeight;

        window.scrollTo({
          top: scrollPosition,
          behavior: 'smooth',
        });
      } catch (error) {
        containerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, []);

  const handleSendMessage = useCallback((): void => {
    if (!inputValue.trim() || loading) {
      return;
    }

    const newQuestion: UserQuestion = {
      question: inputValue.trim(),
      showAnswer: false,
    };

    gtm(
      selectElement({
        element: [
          TRACKING_NAME,
          `Send Customer Question: ${newQuestion.question}`,
        ],
      })
    );

    setInputValue('');
    setLoading(true);

    // Add the question and move to the second slide to show user questions
    setUserQuestions([...userQuestions, newQuestion]);
    setCurrentView('user');

    // Use a timeout to ensure the component has rendered before scrolling
    setTimeout(() => {
      if (containerRef.current && isMobile) {
        scrollToComponent();
      }
    }, 100);

    setTimeout(() => {
      setLoading(false);
      setUserQuestions((prev) =>
        prev.map((q, i) =>
          i === prev.length - 1 ? { ...q, showAnswer: true } : q
        )
      );
    }, 2000);
  }, [inputValue, loading, userQuestions, isMobile, scrollToComponent]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  /**
   * Renders the user questions list with loading states
   */
  const renderUserQuestions = useCallback(
    ({ showButton }) => (
      <>
        {showButton && (
          <button
            className="mb-3 flex items-center gap-1.5 font-black text-primary-main"
            onClick={() => setCurrentView('preset')}
            data-test="aibot-back-button"
          >
            <IconChevronLeft className="h-3 w-3" />
            <span>Zurück zu allen Fragen</span>
          </button>
        )}
        <div
          className="mt-2 flex flex-col gap-2"
          data-test="aibot-user-questions-container"
        >
          {userQuestions.map((userQ, index) => (
            <div key={`user-question-${index}`} className="flex flex-col gap-2">
              <div
                className="text-text-dark-bg-contrast-primary ml-auto w-fit rounded-lg bg-primary-main px-4 py-2"
                data-test={`aibot-user-question-${index}`}
              >
                {userQ.question}
              </div>

              {userQ.showAnswer ? (
                <Alert
                  type="error1"
                  message="Es tut uns leid, aber wir konnten Ihre Frage aufgrund eines technischen Problems auf unserer Seite nicht beantworten. Bitte versuchen Sie es später noch einmal."
                  className="mt-1"
                  data-test={`aibot-user-answer-${index}`}
                />
              ) : (
                <IconAnimatedSpinner className="h-5 w-5 text-primary-main" />
              )}
            </div>
          ))}
        </div>
      </>
    ),
    [userQuestions]
  );

  /**
   * Renders the preset questions list
   */
  const renderPresetQuestions = useCallback(
    () => (
      <>
        <p
          className="mb-3 font-black text-text-light-bg-primary"
          data-test="aibot-questions-title"
        >
          Du hast noch Fragen?
        </p>
        <div
          className="flex flex-col gap-2"
          data-test="aibot-questions-container"
        >
          {qaItems.map((item, index) => (
            <QAItem
              key={`qa-item-${index}`}
              activeIndex={activeIndex}
              answer={item.answer}
              index={index}
              question={item.question}
              trackingName={TRACKING_NAME}
              setActiveIndex={setActiveIndex}
            />
          ))}
        </div>
        {renderUserQuestions({ showButton: false })}
        <div className="mt-2 flex w-full justify-between gap-1 rounded-lg border border-input-light-bg-outline-default px-3 py-1.5 text-text-light-bg-secondary">
          <input
            type="text"
            placeholder="Stelle eine Frage"
            className="w-full outline-hidden"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={loading}
            data-test="aibot-question-input"
          />
          <button
            className={cn(
              'flex items-center justify-center gap-1 rounded-full border border-primary-outlined-resting-border px-2.5 py-2 text-primary-main',
              loading && 'cursor-not-allowed opacity-70'
            )}
            onClick={handleSendMessage}
            disabled={loading || !inputValue.trim()}
            data-test="aibot-send-button"
          >
            <span className="text-xs font-medium">Senden</span>
            <IconSend className="h-4 w-4 shrink-0" />
          </button>
        </div>
      </>
    ),
    [
      activeIndex,
      inputValue,
      loading,
      qaItems,
      handleKeyDown,
      handleSendMessage,
      renderUserQuestions,
    ]
  );

  return (
    <ImpressionTrackerNew element={[TRACKING_NAME]}>
      <div className="mt-6">
        <span ref={containerRef}>
          {currentView === 'preset'
            ? renderPresetQuestions()
            : renderUserQuestions({ showButton: true })}
        </span>
      </div>
    </ImpressionTrackerNew>
  );
};

export default AiBotTest;
